import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../../../components/layout/AppLayout';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { AlertCircle, CheckCircle, XCircle, Eye, Trash2, Settings } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminWantsOffersPage = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expiryDays, setExpiryDays] = useState(30);

  useEffect(() => {
    loadReports();
    loadStats();
    loadSettings();
  }, []);

  const loadReports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wants-offers/reports?status=pending`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wants-offers/stats`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wants-offers/settings`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        setExpiryDays(data.settings?.expiry_days || 30);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleReviewReport = async (reportId, action, adminNotes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wants-offers/reports/${reportId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, admin_notes: adminNotes })
      });

      if (response.ok) {
        toast.success(`Report ${action}ed successfully`);
        loadReports();
        loadStats();
      } else {
        toast.error('Failed to review report');
      }
    } catch (error) {
      toast.error('Failed to review report');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/wants-offers/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ expiry_days: parseInt(expiryDays) })
      });

      if (response.ok) {
        toast.success('Settings updated successfully');
        loadSettings();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Wants & Offers Management</h1>
          <p className="text-muted-foreground">Review posts, handle reports, and manage settings</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Posts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_posts}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Posts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active_posts}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {stats.active_wants} Wants • {stats.active_offers} Offers
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.pending_reports}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Post Expiry</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{settings?.expiry_days || 30} days</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="reports" className="space-y-4">
          <TabsList>
            <TabsTrigger value="reports" className="gap-2">
              <AlertCircle className="h-4 w-4" />
              Reports ({reports.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : reports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No pending reports</h3>
                  <p className="text-muted-foreground">All reports have been reviewed</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reports.map((report) => (
                  <Card key={report.report_id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">
                            {report.post_details?.title || 'Post not found'}
                          </CardTitle>
                          <CardDescription>
                            Reported by @{report.reporter_username} • {report.category}
                          </CardDescription>
                        </div>
                        <Badge variant={report.status === 'pending' ? 'default' : 'secondary'}>
                          {report.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Report Details */}
                      <div>
                        <h4 className="font-semibold mb-2">Report Reason:</h4>
                        <p className="text-sm text-muted-foreground">{report.reason}</p>
                        {report.details && (
                          <p className="text-sm text-muted-foreground mt-2">Details: {report.details}</p>
                        )}
                      </div>

                      {/* Post Details */}
                      {report.post_details && (
                        <div className="border-t pt-4">
                          <h4 className="font-semibold mb-2">Post Content:</h4>
                          <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant={report.post_details.post_type === 'want' ? 'default' : 'secondary'}>
                                {report.post_details.post_type.toUpperCase()}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                by @{report.post_details.username}
                              </span>
                            </div>
                            <p className="text-sm line-clamp-3">{report.post_details.description}</p>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-3 pt-4 border-t">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleReviewReport(report.report_id, 'remove_post', 'Post removed due to violations')}
                          className="gap-2"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove Post
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReviewReport(report.report_id, 'dismiss', 'No violation found')}
                          className="gap-2"
                        >
                          <XCircle className="h-4 w-4" />
                          Dismiss Report
                        </Button>
                        {report.post_details && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(`/wants-offers/${report.post_id}`, '_blank')}
                            className="gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            View Post
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Wants & Offers Settings</CardTitle>
                <CardDescription>Configure post expiry and other settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Post Expiry (Days)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(e.target.value)}
                    className="max-w-xs"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Posts will automatically expire after this many days. Recommended: 30-90 days.
                  </p>
                </div>

                <Button onClick={handleUpdateSettings}>
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};
